const FileType = require('file-type-js');

export const validateFileExtension = async (file: File) => {
  const isMatch = file.name.match(/\.(\w*)$/);
  const extName = isMatch ? isMatch[1].toLocaleLowerCase() : null;
  const arrayBuffer = await file.arrayBuffer();
  const extendedFiletypes = extendFileTypes();
  Object.assign(FileType.fileTypes, extendedFiletypes);
  try {
    return await FileType.is(arrayBuffer, extName);
  } catch {
    // We cannot verify the extensions of some types e.g docx
    return true;
  }
};

const stringToBytes = (string: string) => [...string].map((character) => character.charCodeAt(0));

const startWith = (arr1: number[], arr2: number[]) => {
  if (arr2.length > arr1.length) return false;

  for (const i in arr2) {
    if (arr2[i] !== arr1[i]) return false;
  }

  return true;
};

const extendFileTypes = () => {
  return {
    ac3: [0x0b, 0x77],
    jxr: [0x49, 0x49, 0xbc],
    bpg: [0x42, 0x50, 0x47, 0xfb],
    tif: [0x4d, 0x4d, 0x0, 0x2a],
    nes: [0x4e, 0x45, 0x53, 0x1a],
    rpm: [0xed, 0xab, 0xee, 0xdb],
    eps: [0xc5, 0xd0, 0xd3, 0xc6],
    xz: [0xfd, 0x37, 0x7a, 0x58, 0x5a, 0x00],
    arrow: [0x41, 0x52, 0x52, 0x4f, 0x57, 0x31, 0x00, 0x00],
    glb: [0x67, 0x6c, 0x54, 0x46, 0x02, 0x00, 0x00, 0x00],
    orf: [0x49, 0x49, 0x52, 0x4f, 0x08, 0x00, 0x00, 0x00, 0x18],
    rw2: [0x49, 0x49, 0x55, 0x00, 0x18, 0x00, 0x00, 0x00, 0x88, 0xe7, 0x74, 0xd8],
    ktx: [0xab, 0x4b, 0x54, 0x58, 0x20, 0x31, 0x31, 0xbb, 0x0d, 0x0a, 0x1a, 0x0a],
    ttf: [0x00, 0x01, 0x00, 0x00, 0x00],
    cur: [0x00, 0x00, 0x02, 0x00],
    cfb: [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1],
    mxf: [0x06, 0x0e, 0x2b, 0x34, 0x02, 0x05, 0x01, 0x01, 0x0d, 0x01, 0x02, 0x01, 0x01, 0x02],
    lnk: [
      0x4c, 0x00, 0x00, 0x00, 0x01, 0x14, 0x02, 0x00, 0x00, 0x00, 0x00, 0x00, 0xc0, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00,
      0x46
    ],
    alias: [0x62, 0x6f, 0x6f, 0x6b, 0x00, 0x00, 0x00, 0x00, 0x6d, 0x61, 0x72, 0x6b, 0x00, 0x00, 0x00, 0x00],
    indd: [0x06, 0x06, 0xed, 0xf5, 0xd8, 0x1d, 0x46, 0xe5, 0xbd, 0x31, 0xef, 0xe7, 0xfe, 0x74, 0xb7, 0x1d],
    skp: [
      0xff, 0xfe, 0xff, 0x0e, 0x53, 0x00, 0x6b, 0x00, 0x65, 0x00, 0x74, 0x00, 0x63, 0x00, 0x68, 0x00, 0x55, 0x00, 0x70,
      0x00, 0x20, 0x00, 0x4d, 0x00, 0x6f, 0x00, 0x64, 0x00, 0x65, 0x00, 0x6c, 0x00
    ],
    Z: {
      check: (arr: number[]) => (arr[0] === 0x1f && arr[1] === 0xa0) || (arr[0] === 0x1f && arr[1] === 0x9d)
    },
    mpc: {
      length: 4,
      check: (arr: number[]) => {
        const isValid = startWith(arr, stringToBytes('MP+'));
        if (isValid) {
          return true;
        }
        return startWith(arr, stringToBytes('MPCK'));
      }
    },
    swf: {
      length: 3,
      check: (arr: number[]) => (arr[0] === 0x43 || arr[0] === 0x46) && arr[1] === 0x43 && arr[2] === 0x46
    },
    flif: {
      length: 4,
      check: (arr: number[]) => startWith(arr, stringToBytes('FLIF'))
    },
    psd: {
      length: 4,
      check: (arr: number[]) => startWith(arr, stringToBytes('8BPS'))
    },
    webp: {
      offset: 8,
      length: 4,
      check: (arr: number[]) => startWith(arr, stringToBytes('WEBP'))
    },
    aif: {
      length: 4,
      check: (arr: number[]) => startWith(arr, stringToBytes('FORM'))
    },
    icns: {
      offset: 0,
      length: 4,
      check: (arr: number[]) => startWith(arr, stringToBytes('icns'))
    },
    woff: {
      length: 8,
      check: (arr: number[]) => {
        const isValid = startWith(arr, stringToBytes('wOFF'));
        if (isValid) {
          const slicedArray = arr.slice(4, arr.length);
          return startWith(slicedArray, [0x00, 0x01, 0x00, 0x00]) || startWith(slicedArray, stringToBytes('OTTO'));
        }

        return isValid;
      }
    },
    mid: {
      length: 4,
      check: (arr: number[]) => startWith(arr, stringToBytes('MThd'))
    },
    woff2: {
      length: 8,
      check: (arr: number[]) => {
        const isValid = startWith(arr, stringToBytes('wOF2'));
        if (isValid) {
          const slicedArray = arr.slice(4, arr.length);
          return startWith(slicedArray, [0x00, 0x01, 0x00, 0x00]) || startWith(slicedArray, stringToBytes('OTTO'));
        }

        return isValid;
      }
    },
    pcap: {
      length: 4,
      check: (arr: number[]) => startWith(arr, [0xd4, 0xc3, 0xb2, 0xa1]) || startWith(arr, [0xa1, 0xb2, 0xc3, 0xd4])
    },
    dsf: {
      length: 4,
      check: (arr: number[]) => startWith(arr, stringToBytes('DSD '))
    },
    lz: {
      length: 4,
      check: (arr: number[]) => startWith(arr, stringToBytes('LZIP'))
    },
    flac: {
      length: 4,
      check: (arr: number[]) => startWith(arr, stringToBytes('fLaC'))
    },
    wv: {
      length: 4,
      check: (arr: number[]) => startWith(arr, stringToBytes('wvpk'))
    },
    ape: {
      length: 4,
      check: (arr: number[]) => startWith(arr, stringToBytes('MAC '))
    },
    crx: {
      length: 4,
      check: (arr: number[]) => startWith(arr, stringToBytes('Cr24'))
    },
    cab: {
      length: 4,
      check: (arr: number[]) => startWith(arr, stringToBytes('MSCF')) || startWith(arr, stringToBytes('ISc('))
    },
    amr: {
      length: 5,
      check: (arr: number[]) => startWith(arr, stringToBytes('#!AMR'))
    },
    rtf: {
      length: 5,
      check: (arr: number[]) => startWith(arr, stringToBytes('{\\rtf'))
    },
    stl: {
      length: 6,
      check: (arr: number[]) => startWith(arr, stringToBytes('solid '))
    },
    blend: {
      length: 7,
      check: (arr: number[]) => startWith(arr, stringToBytes('BLENDER'))
    },
    mpg: {
      check: (arr: number[]) => startWith(arr, [0x0, 0x0, 0x1, 0xba]) || startWith(arr, [0x0, 0x0, 0x1, 0xb3])
    },
    raf: {
      length: 16,
      check: (arr: number[]) => startWith(arr, stringToBytes('FUJIFILMCCD-RAW'))
    },
    xm: {
      length: 16,
      check: (arr: number[]) => startWith(arr, stringToBytes('Extended Module:'))
    },
    voc: {
      length: 22,
      check: (arr: number[]) => startWith(arr, stringToBytes('Creative Voice File'))
    },
    s3m: {
      offset: 44,
      length: 4,
      check: (arr: number[]) => startWith(arr, stringToBytes('SCRM'))
    },
    mts: {
      length: 200,
      check: (arr: number[]) => arr[4] === 0x47 && (arr[192] === 0x47 || arr[196] === 0x47)
    },
    mobi: {
      offset: 60,
      length: 8,
      check: (arr: number[]) => startWith(arr, [0x42, 0x4f, 0x4f, 0x4b, 0x4d, 0x4f, 0x42, 0x49])
    },
    dcm: {
      offset: 60,
      length: 4,
      check: (arr: number[]) => startWith(arr, [0x44, 0x49, 0x43, 0x4d])
    }
  };
};
